/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Home as HomeIcon, Building2, Sparkles, ChefHat, Hammer, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { api, BRAND, SERVICES, resolveImageUrl } from "@/lib/divine";

const serviceIcons = {
  "Residential": HomeIcon,
  "Commercial": Building2,
  "Vastu": Sparkles,
  "Modular Kitchen": ChefHat,
  "Home Renovation": Hammer,
};

export default function Home() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects").then((r) => {
  const list = Array.isArray(r.data) ? r.data : (r.data.projects || []);
  setProjects(list.slice(0, 6));
}).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden grain">
        <img
          src="https://images.pexels.com/photos/13722826/pexels-photo-13722826.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920"
          alt="Luxury interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/40" />
        <div className="absolute inset-0 bg-black/40" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28 w-full text-white">
          <div className="reveal max-w-4xl">
            <div className="overline mb-6">{BRAND.tagline}</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl tracking-tighter leading-[0.95] text-white">
              Spaces of <span className="italic text-[#D4A437]">soul</span>,
              <br />
              built on <span className="italic text-[#D4A437]">science</span>.
            </h1>
            <p className="text-white/85 mt-8 max-w-xl text-base md:text-lg leading-relaxed">
              Divine Interiors crafts luxurious, harmonious interiors rooted in modern aesthetics and ancient Vastu wisdom — designed to make your home or business breathe.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/booking" data-testid="hero-book-btn" className="btn-gold">
                Book Consultation <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link to="/projects" data-testid="hero-portfolio-btn" className="btn-ghost !text-white !border-white hover:!bg-white hover:!text-[#0A0A0A]">
                Explore Portfolio <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>

          {/* Marquee strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              ["120+", "Projects"],
              ["14+", "Years"],
              ["100%", "Vastu Aligned"],
              ["5★", "Client Rated"],
            ].map(([k, v]) => (
              <div key={v} className="bg-[#0A0A0A]/60 backdrop-blur p-6 md:p-8">
                <div className="font-serif text-3xl md:text-4xl text-[#D4A437]">{k}</div>
                <div className="text-xs tracking-[0.2em] uppercase text-white/70 mt-2">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-24 md:py-32 mandala-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline mb-6">Our Philosophy</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              The science of <span className="italic text-[#D4A437]">space</span>.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <p className="text-lg text-[#0A0A0A]/85 leading-relaxed font-serif">
              Every room has a rhythm. Every wall holds a story. We design with both — listening to your life and to the timeless principles of Vastu Shastra.
            </p>
            <p className="text-[#6B6B6B] leading-relaxed">
              The result isn't just beautiful interiors. It's spaces that feel right the moment you walk in — calming, balanced, and quietly powerful.
            </p>
            <Link to="/about" data-testid="philosophy-more-btn" className="inline-flex items-center gap-2 text-[#D4A437] hover:text-[#E5B547] mt-4 text-sm tracking-widest uppercase">
              About the Studio <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* AWARD */}
      <section className="py-24 md:py-32 border-t border-black/5 bg-[#FAF7F0]" data-testid="award-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="overline mb-6 inline-flex items-center gap-3">
              <span className="w-8 h-px bg-[#D4A437]"></span> Honoured
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Award-winning
              <br />
              <span className="italic text-[#D4A437]">recognition</span>.
            </h2>
            <p className="text-[#0A0A0A]/85 mt-6 text-lg leading-relaxed font-serif">
              "Promising and Innovative Interior Design & Execution Firm of the Year" — India Trendsetter Design Awards, Goa 2024.
            </p>
            <p className="text-[#0A0A0A]/85 mt-4 text-lg leading-relaxed font-serif">
              Ratnesh Shah, Founder — featured in <span className="text-[#D4A437]">Top Interiors India · 40 Under 40, 2025</span>.
            </p>
            <p className="text-[#0A0A0A]/85 mt-4 text-lg leading-relaxed font-serif">
              Certified Vastu Consultant — <span className="text-[#D4A437]">Advance Vastu Course (Swarg'26)</span> by Acharya Pankit Goyal, Jan 2026.
            </p>
            <p className="text-[#6B6B6B] mt-6 leading-relaxed">
              Grateful and honoured to be recognised — every award is a testament to our passion for creativity and excellence in every project.
            </p>
            <p className="text-xs tracking-[0.2em] uppercase text-[#D4A437] mt-8">
              Thank you · @beginup_com · Top Interiors India · Acharya Pankit Goyal
            </p>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="grid grid-cols-1 gap-5">
              <div className="zoomable border border-[#D4A437]/30 bg-white p-3 md:p-4 shadow-[0_30px_80px_-20px_rgba(212,164,55,0.25)]">
                <img
                  src="https://shobhanirmiti.com/wp-content/uploads/2024/07/Award-2024-1024x683.jpg"
                  alt="India Trendsetter Design Awards — Goa 2024"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <div className="px-2 py-3 text-center">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4A437]">2024 · Goa</div>
                  <div className="font-serif text-base mt-1">India Trendsetter Design Awards</div>
                </div>
              </div>
              <div className="zoomable border border-[#D4A437]/30 bg-white p-3 md:p-4 shadow-[0_30px_80px_-20px_rgba(212,164,55,0.25)]" data-testid="award-40under40">
                <img
                  src="https://customer-assets.emergentagent.com/job_home-harmony-space/artifacts/p32dzkno_IMG_1547.JPG"
                  alt="Top Interiors India — 40 Under 40 · 2025"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <div className="px-2 py-3 text-center">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4A437]">2025 · National</div>
                  <div className="font-serif text-base mt-1">Top Interiors India — 40 Under 40</div>
                </div>
              </div>
              <div className="zoomable border border-[#D4A437]/30 bg-white p-3 md:p-4 shadow-[0_30px_80px_-20px_rgba(212,164,55,0.25)]" data-testid="award-swarg26">
                <img
                  src="https://customer-assets.emergentagent.com/job_home-harmony-space/artifacts/auktidi7_WhatsApp%20Image%202026-06-30%20at%2012.32.53%20PM.jpeg"
                  alt="Swarg'26 — Advance Vastu Course ceremony with Acharya Pankit Goyal"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <div className="px-2 py-3 text-center">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4A437]">2026 · Jan</div>
                  <div className="font-serif text-base mt-1">Certified Vastu Consultant</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES — asymmetric bento */}
      <section className="py-24 md:py-32 border-t border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <div className="overline mb-4">What We Offer</div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                Crafted <span className="italic text-[#D4A437]">services</span>.
              </h2>
            </div>
            <p className="max-w-md text-[#6B6B6B]">
              From a single Vastu consultation to a full home transformation — we bring the same intention to every project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {SERVICES.map((s, idx) => {
              const Icon = serviceIcons[s.key];
              const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4"];
              return (
                <div
                  key={s.key}
                  data-testid={`service-card-${s.key.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`${spans[idx]} group bg-[#F5F0E5] border border-black/5 hover:border-[#D4A437]/40 p-8 md:p-10 transition-all duration-500 cursor-default`}
                >
                  <Icon className="w-8 h-8 text-[#D4A437] mb-8" strokeWidth={1.3} />
                  <h3 className="font-serif text-2xl md:text-3xl text-[#0A0A0A] leading-tight">{s.title}</h3>
                  <p className="text-[#6B6B6B] mt-4 leading-relaxed">{s.desc}</p>
                  <div className="mt-8 flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#D4A437] opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24 md:py-32 border-t border-black/5 mandala-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="overline mb-4">Transparent Pricing</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Honest <span className="italic text-[#D4A437]">numbers</span>.
            </h2>
            <p className="text-[#6B6B6B] mt-6">No hidden fees. No surprises. Just clarity from the very first conversation.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div data-testid="pricing-vastu" className="border border-black/10 bg-[#FAF7F0] p-10 md:p-12 hover:border-[#D4A437]/50 transition-colors">
              <div className="overline mb-6">Vastu Consultation</div>
              <div className="font-serif text-5xl md:text-6xl">
                ₹11,000<span className="text-base text-[#6B6B6B] font-sans ml-2">/ session</span>
              </div>
              <ul className="mt-8 space-y-3 text-[#6B6B6B] text-sm">
                <li className="flex gap-3"><span className="text-[#D4A437]">—</span> Site visit & detailed analysis</li>
                <li className="flex gap-3"><span className="text-[#D4A437]">—</span> Personalised Vastu report</li>
                <li className="flex gap-3"><span className="text-[#D4A437]">—</span> Practical remedies & guidance</li>
              </ul>
              <Link to="/booking" className="btn-ghost mt-10">Book Now <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} /></Link>
            </div>

            <div data-testid="pricing-interior" className="border border-[#D4A437] bg-gradient-to-b from-[#FFF8E6] to-[#FAF7F0] p-10 md:p-12 relative">
              <span className="absolute top-6 right-6 text-[10px] tracking-[0.25em] uppercase text-[#D4A437] border border-[#D4A437] px-3 py-1">Popular</span>
              <div className="overline mb-6">Interior Design</div>
              <div className="font-serif text-5xl md:text-6xl">
                ₹200<span className="text-base text-[#6B6B6B] font-sans ml-2">/ sft onwards</span>
              </div>
              <ul className="mt-8 space-y-3 text-[#6B6B6B] text-sm">
                <li className="flex gap-3"><span className="text-[#D4A437]">—</span> End-to-end design execution</li>
                <li className="flex gap-3"><span className="text-[#D4A437]">—</span> 3D visualisations & moodboards</li>
                <li className="flex gap-3"><span className="text-[#D4A437]">—</span> Vastu-aligned planning included</li>
              </ul>
              <Link to="/booking" className="btn-gold mt-10">Book Now <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROJECTS PREVIEW */}
      <section className="py-24 md:py-32 border-t border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="overline mb-4">Selected Works</div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                Recent <span className="italic text-[#D4A437]">projects</span>.
              </h2>
            </div>
            <Link to="/projects" data-testid="home-view-all-projects" className="text-sm tracking-widest uppercase text-[#D4A437] hover:text-[#E5B547] inline-flex items-center gap-2">
              View All <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            {projects.map((p, idx) => {
              const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-12"];
              const heights = ["h-[400px] md:h-[520px]", "h-[400px] md:h-[520px]", "h-[300px] md:h-[360px]", "h-[300px] md:h-[360px]", "h-[300px] md:h-[360px]", "h-[400px] md:h-[500px]"];
              return (
                <Link
                  to="/projects"
                  key={p.id}
                  data-testid={`home-project-${idx}`}
                  className={`${spans[idx]} ${heights[idx]} zoomable relative group block overflow-hidden`}
                >
                  <img src={resolveImageUrl(p.image)} alt={p.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <div className="overline mb-2">{p.category}</div>
                    <h3 className="font-serif text-xl md:text-2xl">{p.title}</h3>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-24 md:py-32 border-t border-black/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="overline mb-8">Kind Words</div>
          <p className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.2] text-[#0A0A0A]">
            "Working with Divine Interiors felt less like a renovation and more like coming home. Every corner finally feels <span className="italic text-[#D4A437]">like ours</span>."
          </p>
          <div className="mt-10 text-sm tracking-widest uppercase text-[#6B6B6B]">
            — Priya & Rohan · Pune
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="border-t border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 flex flex-col md:flex-row gap-6 items-center justify-between">
          <h3 className="font-serif text-2xl md:text-3xl">Ready to begin? Let's talk.</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/booking" data-testid="cta-book" className="btn-gold">Book Consultation</Link>
            <a href={`tel:${BRAND.phoneRaw}`} data-testid="cta-call" className="btn-ghost">
              <Phone className="w-4 h-4" strokeWidth={1.5} /> {BRAND.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
